import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

import ROUTES from '../../routes';
import api from '../../config/axiosInstance';

import { showToast } from '../../utils/toast';
import { handleErrors } from '../../utils/common';

const resolveImageUrl = (img, baseUrl) => {
    if (!img) return '';
    return img.startsWith('http') ? img : baseUrl + img;
};

function EditBlog() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const formRef = useRef();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [img, setImg] = useState(null);
    const [isFeatured, setIsFeatured] = useState(false);
    const [imgPreview, setImgPreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBlog = async () => {
            setLoading(true);
            try {
                const res = await api.get(`/blogs/${slug}`);
                if (res.status === 200) {
                    const blog = res.data.blog;
                    const imageBaseUrl = res.data.base_url || '';
                    setTitle(blog.title || '');
                    setDescription(blog.description || '');
                    setIsFeatured(blog.is_featured || false);
                    if (blog.img) {
                        setImgPreview(resolveImageUrl(blog.img, imageBaseUrl));
                    }
                }
            } catch (err) {
                handleErrors(err);
            } finally {
                setLoading(false);
            }
        };

        fetchBlog();

        const $ = window.jQuery;

        $.validator.addMethod('extension', function (value, element, allowedExtensions) {
            if (!value) return true;
            var extension = value.split('.').pop().toLowerCase();
            return $.inArray(extension, allowedExtensions) !== -1;
        }, 'Invalid file type');

        $.validator.addMethod('ckeditorRequired', function (value) {
            const textOnly = value.replace(/<[^>]*>/g, '').trim();
            return textOnly.length > 0;
        }, 'This field is required');

        if (formRef.current && $.fn.validate) {
            $(formRef.current).validate({
                rules: {
                    title: {
                        required: true,
                        maxlength: 500
                    },
                    description: {
                        required: true,
                        ckeditorRequired: true
                    },
                    img: {
                        required: false,
                        extension: ['jpg', 'jpeg', 'png', 'svg', 'webp']
                    },
                    isFeatured: {
                        required: true
                    }
                },
                messages: {
                    title: {
                        required: "Please enter blog title",
                        maxlength: "Title cannot exceed 500 characters"
                    },
                    description: {
                        required: "Please enter blog description"
                    },
                    img: {
                        extension: "Only jpg, jpeg, png, svg, webp allowed"
                    },
                    isFeatured: {
                        required: "Please select is featured"
                    }
                },
                ignore: ".ck-editor *",
                errorElement: "label",
                validClass: "is-valid",
                errorClass: "is-invalid text-danger",
                highlight: function (element, errorClass, validClass) {
                    $(element).addClass(errorClass).removeClass(validClass);
                },
                unhighlight: function (element, errorClass, validClass) {
                    $(element).removeClass(errorClass).addClass(validClass);
                }
            });
        }
    }, [slug]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImg(file);
            setImgPreview(URL.createObjectURL(file));
        }
    };

    const handleFormSubmit = async () => {
        const $ = window.jQuery;
        const form = $(formRef.current);

        if (form.valid()) {
            $(form)
                .find('.is-valid, .is-invalid')
                .removeClass('is-valid is-invalid');

            $(form)
                .find('label.error, .invalid-feedback')
                .remove();

            const payload = { title, description, is_featured: isFeatured };
            if (img) {
                payload.img = img;
            }

            try {
                setSubmitting(true);
                const res = await api.put(
                    `/blogs/${slug}`,
                    payload,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data"
                        }
                    }
                );
                if (res.status === 200) {
                    showToast("success", res.data.message, "Success");
                    navigate(ROUTES.BLOGS);
                }
            } catch (err) {
                handleErrors(err);
            } finally {
                setSubmitting(false);
            }
        }
    };

    if (loading) {
        return (
            <div className="text-center my-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.BLOGS}>Blogs</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to={`/blogs/${slug}/edit`}>Edit Blog</Link>
                    </li>
                </ol>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h4 className="card-title">Edit Blog</h4>
                        </div>
                        <div className="card-body">
                            <form ref={formRef} noValidate>
                                <div className="row">
                                    <div className="mb-3 col-lg-6 col-md-6">
                                        <label className="form-label">Title<sup style={{ color: 'red' }}>*</sup></label>
                                        <input
                                            name="title"
                                            type="text"
                                            className="form-control"
                                            placeholder="Ex. How to maintain your car"
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}
                                        />
                                    </div>
                                    <div className="mb-3 col-lg-6 col-md-6">
                                        <label htmlFor="img" className="form-label">Image</label>
                                        <div className="input-group custom_file_input">
                                            <div className="form-file">
                                                <input
                                                    name="img"
                                                    type="file"
                                                    className="form-file-input form-control"
                                                    onChange={handleImageChange}
                                                />
                                            </div>
                                            {/* <span className="input-group-text">Upload</span> */}
                                        </div>
                                        {imgPreview && (
                                            <div className="mt-2">
                                                <img
                                                    src={imgPreview}
                                                    alt="Preview"
                                                    style={{ width: 100, height: 100, objectFit: 'contain' }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3 col-lg-12">
                                        <label className="form-label">Description<sup style={{ color: 'red' }}>*</sup></label>
                                        <CKEditor
                                            editor={ClassicEditor}
                                            config={{
                                                toolbar: [
                                                    'heading', '|',
                                                    'bold', 'italic', 'link', 'code', 'codeBlock',
                                                    '|', 'bulletedList', 'numberedList'
                                                ]
                                            }}
                                            data={description}
                                            onChange={(event, editor) => {
                                                const data = editor.getData();
                                                setDescription(data);
                                                const hiddenInput = document.querySelector('input[name="description"]');
                                                if (hiddenInput) hiddenInput.value = data;
                                            }}
                                        />
                                        <input
                                            type="hidden"
                                            name="description"
                                            value={description}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <label htmlFor="isFeatured">Is Featured</label>
                                            <select
                                                name="isFeatured"
                                                id="isFeatured"
                                                className="form-control"
                                                value={isFeatured}
                                                onChange={(e) => setIsFeatured(e.target.value)}
                                            >
                                                <option value="">Select</option>
                                                <option value="true">Yes</option>
                                                <option value="false">No</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-3">
                                    <button
                                        type="button"
                                        onClick={handleFormSubmit}
                                        className="btn btn-sm btn-primary me-2"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <i className="spinner-border spinner-border-sm me-1"></i>
                                                Submit
                                            </>
                                        ) : (
                                            <>
                                                <i className="fa fa-floppy-disk me-1"></i>
                                                Submit
                                            </>
                                        )}
                                    </button>
                                    <Link to={ROUTES.BLOGS} className="btn btn-sm btn-danger light">
                                        <i className="fas fa-times-circle"></i>&nbsp;
                                        Cancel
                                    </Link>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default EditBlog;
